import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { LegacyImage, Markdown } from "components";
import { useAnchor } from "hooks";
import { CheckIcon, Features5Background } from "icons";
import { getImageFormat } from "lib/utils";
import { Features5Content } from "types/graphql/generated";

interface Props {
  content: Features5Content;
}

const Features5: VFC<Props> = withErrorBoundary(
  ({
    content: {
      title,
      featuresList,
      featuresCard: {
        title: cardTitle,
        icon: {
          data: {
            icon: { ext, ...icon },
          },
        },
        stepOne,
        stepTwo,
        stepThree,
        stepFour,
      },
    },
  }) => {
    useAnchor("features5");

    return (
      <StyledFeatures5 className="features5 Features5 Content">
        <div className="Features5__cardBackground">
          <div className="Features5__card">
            <ul className="Features5__cardContentList">
              <li className="Features5__cardTitle Font_18_26_700  Color_primary">{cardTitle}</li>
              <li>
                <LegacyImage
                  {...icon}
                  layout="fill"
                  transform={{ format: getImageFormat(ext) }}
                  className="Features5__image"
                />
              </li>
              <li className="Features5__cardStepOne Font_14_20_600 md:Font_12_16_600">
                <CheckIcon />
                {stepOne}
              </li>
              <li className="Features5__cardStepTwo Font_14_20_600 md:Font_12_16_600">
                <CheckIcon />
                {stepTwo}
              </li>
              <li className="Features5__cardStepThree Font_14_20_600 md:Font_12_16_600">
                <CheckIcon />
                {stepThree}
              </li>
              <li className="Features5__cardStepFour Font_14_20_600 md:Font_12_16_600">
                <CheckIcon />
                {stepFour}
              </li>
            </ul>
            <div className="Features5__cardWhiteGradient" />
          </div>
          <Features5Background className="Features5__background" />
        </div>
        <div className="Features5__textContainer">
          <h2 className="Font_48_54_600 md:Font_40_48_600">{title}</h2>
          <ul className="Features5__featuresList">
            {featuresList.map(({ id, title, description }) => (
              <li key={id} className="Features5__singleFeature">
                <h2 className="Font_24_32_600 Color_primary">{title}</h2>
                <Markdown className="Features5__singleFeatureDescription Color_secondary Font_18_26_450">
                  {description}
                </Markdown>
              </li>
            ))}
          </ul>
        </div>
      </StyledFeatures5>
    );
  },
  {}
);

const StyledFeatures5 = styled.section`
  display: flex;
  justify-content: space-between;
  margin-top: -64px;
  padding-top: 64px;

  .Features5__cardBackground {
    position: relative;
    max-width: 392px;
    min-width: 360px;
    max-height: 382px;
    width: 100%;
    background: ${({ theme }) => theme.colors.fog["20"]};
    display: flex;
    align-items: center;
    padding-top: 48px;
    overflow: hidden;
  }

  .Features5__background {
    position: absolute;
    top: 0;
    z-index: 0;
  }

  .Features5__card {
    z-index: 1;
    margin: 0 auto;
    padding: 30px 20px 0;
    background-color: ${({ theme }) => theme.colors.fog["00"]};
    width: fit-content;
    border-radius: 28px 28px 0 0;
  }

  .Features5__cardContentList {
    margin-left: 25px;
    list-style: none;
    border-left: 1px solid #c4c4c4;
    margin-bottom: -78px;

    li {
      margin-left: -25px;
    }

    div {
      margin-left: unset;
    }

    li:nth-child(2) {
      margin-left: -22px;
      margin-top: 16px;
      width: 45px;
      height: 45px;
      border-radius: 50%;
      box-shadow:
        0 0.874px 0 rgb(25 24 23 / 4%),
        0 3.4961px 13.9842px rgb(25 24 23 / 16%);
    }
  }

  .Features5__cardTitle {
    color: ${({ theme }) => theme.colors.fog["00"]};
    background-color: #000;
    padding: 10px 21px;
    border-radius: 44px;
    width: fit-content;
  }

  .Features5__image {
    position: relative;
    width: 45px;
    height: 45px;
    border-radius: 50%;
    animation: animate-pulse 3s linear infinite;

    @keyframes animate-pulse {
      0% {
        box-shadow:
          0 0 0 0 rgb(228 228 255 / 50%),
          0 0 0 0 rgb(228 228 255 / 100%);
      }

      40% {
        box-shadow:
          0 0 0 30px rgb(228 228 255 / 50%),
          0 0 0 0 rgb(228 228 255 / 100%);
      }

      80% {
        box-shadow:
          0 0 0 60px rgb(255 109 74 / 0%),
          0 0 0 30px rgb(255 109 74 / 0%);
      }

      100% {
        box-shadow:
          0 0 0 600px rgb(255 109 74 / 0%),
          0 0 0 60px rgb(255 109 74 / 0%);
      }
    }
  }

  .Features5__cardStepOne {
    display: flex;
    align-items: center;
    margin-top: 16px;
    color: ${({ theme }) => theme.colors.coral["80"]};
    background-color: ${({ theme }) => theme.colors.dawn["40"]};
    padding: 8px 16px;
    border-radius: 65px;
    width: fit-content;

    svg {
      margin-right: 8px;
      width: 12px;
      height: 12px;
      stroke: ${({ theme }) => theme.colors.coral["80"]};
    }
  }

  .Features5__cardStepTwo {
    display: flex;
    align-items: center;
    margin-top: 16px;
    color: ${({ theme }) => theme.colors.dusk["70"]};
    background-color: #d6cdff;
    padding: 8px 16px;
    border-radius: 65px;
    width: fit-content;

    svg {
      margin-right: 8px;
      width: 12px;
      height: 12px;
      stroke: ${({ theme }) => theme.colors.dusk["70"]};
    }
  }

  .Features5__cardStepThree {
    display: flex;
    align-items: center;
    margin-top: 16px;
    color: ${({ theme }) => theme.colors.eucalyptus["80"]};
    background-color: ${({ theme }) => theme.colors.eucalyptus["30"]};
    padding: 8px 16px;
    border-radius: 65px;
    width: fit-content;

    svg {
      margin-right: 8px;
      width: 12px;
      height: 12px;
      stroke: ${({ theme }) => theme.colors.eucalyptus["80"]};
    }
  }

  .Features5__cardStepFour {
    display: flex;
    align-items: center;
    margin-top: 16px;
    color: ${({ theme }) => theme.colors.coral["80"]};
    background-color: ${({ theme }) => theme.colors.coral["40"]};
    padding: 8px 16px;
    border-radius: 65px;
    width: fit-content;

    svg {
      margin-right: 8px;
      width: 12px;
      height: 12px;
      stroke: ${({ theme }) => theme.colors.coral["80"]};
    }
  }

  .Features5__cardWhiteGradient {
    max-width: 298px;
    width: 100%;
    height: 70px;
    background: linear-gradient(0deg, rgb(255 255 255 / 50%) 0%, rgb(255 255 255 / 0%) 100%);
    mix-blend-mode: screen;
  }

  .Features5__textContainer {
    margin-left: 72px;
    max-width: 728px;
  }

  .Features5__featuresList {
    display: flex;
    justify-content: space-between;
    margin-top: 48px;
    list-style: none;
  }

  .Features5__singleFeature {
    max-width: 328px;

    &:not(:first-child) {
      margin-left: 40px;
    }
  }

  .Features5__singleFeatureDescription {
    margin-top: 16px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    flex-direction: column;

    .Features5__cardBackground {
      min-width: unset;
      padding-top: 40px;
    }

    .Features5__textContainer {
      margin-top: 48px;
      margin-left: unset;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.sm}px`}) {
    .Features5__featuresList {
      margin-top: 40px;
      flex-direction: column;
    }

    .Features5__singleFeature {
      max-width: unset;

      &:not(:first-child) {
        margin-top: 32px;
        margin-left: unset;
      }
    }
  }
`;

gql`
  fragment Features5Content on ComponentFeaturesFeatures5 {
    title
    featuresList {
      id
      title
      description
    }
    featuresCard {
      title
      stepOne
      stepTwo
      stepThree
      stepFour
      icon {
        data {
          icon: attributes {
            src: hash
            width
            height
            alt: alternativeText
            ext
          }
        }
      }
    }
  }
`;

export { Features5 };
export type { Props as Features5Props };
